import React, { useEffect, useState } from 'react';
import { Card, Row, Col, Spinner, Button, Alert } from 'react-bootstrap';
import { Bar, Line, Pie, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import axiosInstance from '../../utils/setupAxios';
import { canViewPaymentAdmin } from '../../utils/authConfig';
import {
  getTopPayingDevices,
  getTopProductsByRevenue,
  getLast7DaysPaymentRevenue,
} from '../../services/paymentService';
import { shortProductLabel, formatUsdLabel, formatChartDayLabel } from '../../utils/chartLabels';
import { getLanguageDisplayName } from '../../utils/languageConstants';
import Pagination from '../common/Pagination';

const LANGUAGE_TABLE_PAGE_SIZE = 5;

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels
);

const Statistics = () => {
  const [topDevices, setTopDevices] = useState({ loading: false, data: null, error: null });
  const [topDays, setTopDays] = useState({ loading: false, data: null, error: null });
  const [topServices, setTopServices] = useState({ loading: false, data: null, error: null });
  const [requestTypes, setRequestTypes] = useState({ loading: false, data: null, error: null });
  const [languages, setLanguages] = useState({ loading: false, data: null, error: null });
  const [languageTablePage, setLanguageTablePage] = useState(1);
  const [processingTime, setProcessingTime] = useState({ loading: false, data: null, error: null });
  const [last7DaysDone, setLast7DaysDone] = useState({ loading: false, data: null, error: null });
  const [clearCacheState, setClearCacheState] = useState({ loading: false, data: null, error: null });
  const showPaymentStats = canViewPaymentAdmin();
  const [topPayingDevices, setTopPayingDevices] = useState({ loading: false, data: null, error: null });
  const [topPaymentProducts, setTopPaymentProducts] = useState({ loading: false, data: null, error: null });
  const [last7DaysRevenue, setLast7DaysRevenue] = useState({ loading: false, data: [], error: null });

  const fetchTopDevices = async () => {
    setTopDevices(prev => ({ ...prev, loading: true, error: null }));
    try {
      const res = await axiosInstance.get('/songs/statistics/top-devices');
      setTopDevices({ loading: false, data: res.data, error: null });
    } catch (error) {
      setTopDevices({ loading: false, data: null, error: error.response?.data?.message || error.message || 'Failed to fetch top devices' });
    }
  };

  const fetchTopDays = async () => {
    setTopDays(prev => ({ ...prev, loading: true, error: null }));
    try {
      const res = await axiosInstance.get('/songs/statistics/top-days');
      setTopDays({ loading: false, data: res.data, error: null });
    } catch (error) {
      setTopDays({ loading: false, data: null, error: error.response?.data?.message || error.message || 'Failed to fetch top days' });
    }
  };

  const fetchTopServices = async () => {
    setTopServices(prev => ({ ...prev, loading: true, error: null }));
    try {
      const res = await axiosInstance.get('/songs/statistics/top-services');
      setTopServices({ loading: false, data: res.data, error: null });
    } catch (error) {
      setTopServices({ loading: false, data: null, error: error.response?.data?.message || error.message || 'Failed to fetch top services' });
    }
  };

  const fetchRequestTypes = async () => {
    setRequestTypes(prev => ({ ...prev, loading: true, error: null }));
    try {
      const res = await axiosInstance.get('/songs/statistics/request-types');
      setRequestTypes({ loading: false, data: res.data, error: null });
    } catch (error) {
      setRequestTypes({ loading: false, data: null, error: error.response?.data?.message || error.message || 'Failed to fetch request types' });
    }
  };

  const fetchLanguages = async () => {
    setLanguages((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const res = await axiosInstance.get('/songs/statistics/languages');
      setLanguages({ loading: false, data: res.data, error: null });
    } catch (error) {
      setLanguages({
        loading: false,
        data: null,
        error: error.response?.data?.message || error.message || 'Failed to fetch language statistics',
      });
    }
  };

  const fetchProcessingTime = async () => {
    setProcessingTime(prev => ({ ...prev, loading: true, error: null }));
    try {
      const res = await axiosInstance.get('/songs/statistics/processing-time');
      setProcessingTime({ loading: false, data: res.data, error: null });
    } catch (error) {
      setProcessingTime({ loading: false, data: null, error: error.response?.data?.message || error.message || 'Failed to fetch processing time' });
    }
  };

  const fetchLast7DaysDone = async () => {
    setLast7DaysDone(prev => ({ ...prev, loading: true, error: null }));
    try {
      const res = await axiosInstance.get('/songs/statistics/last-7-days-done');
      setLast7DaysDone({ loading: false, data: res.data, error: null });
    } catch (error) {
      setLast7DaysDone({ loading: false, data: null, error: error.response?.data?.message || error.message || 'Failed to fetch last 7 days data' });
    }
  };


  const fetchTopPayingDevices = async () => {
    setTopPayingDevices((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const res = await getTopPayingDevices();
      setTopPayingDevices({ loading: false, data: res.data, error: null });
    } catch (error) {
      setTopPayingDevices({
        loading: false,
        data: null,
        error: error.response?.data?.message || error.message || 'Failed to fetch top paying devices',
      });
    }
  };

  const fetchTopPaymentProducts = async () => {
    setTopPaymentProducts((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const res = await getTopProductsByRevenue();
      setTopPaymentProducts({ loading: false, data: res.data, error: null });
    } catch (error) {
      setTopPaymentProducts({
        loading: false,
        data: null,
        error: error.response?.data?.message || error.message || 'Failed to fetch top products',
      });
    }
  };

  const fetchLast7DaysRevenue = async () => {
    setLast7DaysRevenue((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const res = await getLast7DaysPaymentRevenue();
      if (res.data?.success && Array.isArray(res.data?.data)) {
        setLast7DaysRevenue({ loading: false, data: res.data.data, error: null });
      } else {
        setLast7DaysRevenue({
          loading: false,
          data: [],
          error: res.data?.message || 'Failed to fetch last 7 days revenue',
        });
      }
    } catch (error) {
      setLast7DaysRevenue({
        loading: false,
        data: [],
        error: error.response?.data?.message || error.message || 'Failed to fetch last 7 days revenue',
      });
    }
  };

  const fetchAllStatistics = async () => {
    const tasks = [
      fetchTopDevices(),
      fetchTopDays(),
      fetchTopServices(),
      fetchRequestTypes(),
      fetchLanguages(),
      fetchProcessingTime(),
      fetchLast7DaysDone(),
    ];
    if (showPaymentStats) {
      tasks.push(fetchTopPayingDevices(), fetchTopPaymentProducts(), fetchLast7DaysRevenue());
    }
    await Promise.all(tasks);
  };

  useEffect(() => {
    fetchAllStatistics();
  }, []);

  useEffect(() => {
    setLanguageTablePage(1);
  }, [languages.data]);

  const handleClearCache = async () => {
    if (window.confirm('Are you sure you want to clear the statistics cache? This will refresh all data.')) {
      setClearCacheState({ loading: true, data: null, error: null });
      try {
        const res = await axiosInstance.post('/songs/statistics/clear-cache');
        if (res.data.success) {
          setClearCacheState({ loading: false, data: res.data, error: null });
          // Refetch all statistics after clearing cache (includes last7DaysDone)
          await fetchAllStatistics();
        } else {
          setClearCacheState({ loading: false, data: null, error: res.data.message || 'Failed to clear cache' });
        }
      } catch (error) {
        setClearCacheState({ loading: false, data: null, error: error.response?.data?.message || error.message || 'Failed to clear cache' });
      }
    }
  };

  const topPayingDevicesItems = topPayingDevices.data?.success ? (topPayingDevices.data.data || []) : [];
  const topPayingDevicesChartData = topPayingDevicesItems.length ? {
    labels: topPayingDevicesItems.map((item) => item.device_id?.slice(0, 8) + '…'),
    datasets: [{
      label: 'Revenue (USD)',
      data: topPayingDevicesItems.map((item) => item.total_revenue_usd),
      backgroundColor: 'rgba(40, 167, 69, 0.75)',
      borderColor: 'rgba(40, 167, 69, 1)',
      borderWidth: 1,
      barThickness: 28,
      maxBarThickness: 32,
    }],
  } : null;

  const topPaymentProductItems = topPaymentProducts.data?.success ? topPaymentProducts.data.data : [];

  const topPaymentProductsChartData = topPaymentProductItems.length ? {
    labels: topPaymentProductItems.map((item) => shortProductLabel(item.product_id, 20)),
    datasets: [{
      label: 'Revenue (USD)',
      data: topPaymentProductItems.map((item) => item.total_revenue_usd),
      backgroundColor: 'rgba(255, 193, 7, 0.75)',
      borderColor: 'rgba(255, 193, 7, 1)',
      borderWidth: 1,
      barThickness: 28,
      maxBarThickness: 32,
    }],
  } : null;

  const topPaymentProductFullLabels = topPaymentProductItems.map((item) => item.product_id || '');

  const last7DaysRevenueItems = Array.isArray(last7DaysRevenue.data) ? last7DaysRevenue.data : [];
  const last7DaysRevenueChartData = last7DaysRevenueItems.length ? {
    labels: last7DaysRevenueItems.map((item) => formatChartDayLabel(item.date)),
    datasets: [{
      label: 'Revenue (USD)',
      data: last7DaysRevenueItems.map((item) => Number(item.total_revenue_usd || 0)),
      backgroundColor: 'rgba(40, 167, 69, 0.75)',
      borderColor: 'rgba(40, 167, 69, 1)',
      borderWidth: 1,
      borderRadius: 6,
      maxBarThickness: 48,
    }],
  } : null;

  const last7DaysRevenueChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: { top: 24 },
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (context) => `Revenue: ${formatUsdLabel(context.parsed.y)}`,
          afterLabel: (context) => {
            const row = last7DaysRevenueItems[context.dataIndex];
            return `Purchases: ${row?.purchase_count ?? 0}`;
          },
        },
      },
      datalabels: {
        display: true,
        anchor: 'end',
        align: 'top',
        clamp: true,
        color: '#566a7f',
        font: { weight: '700', size: 11 },
        formatter: (value) => formatUsdLabel(value),
      },
    },
    scales: {
      x: {
        grid: { display: false },
      },
      y: {
        beginAtZero: true,
        ticks: {
          callback: (value) => formatUsdLabel(value),
        },
      },
    },
  };

  // Chart data for Top Devices
  const topDevicesChartData = topDevices.data?.success ? {
    labels: topDevices.data.data.map(item => item.device_id),
    datasets: [
      {
        label: 'Completed Requests',
        data: topDevices.data.data.map(item => item.completed_count),
        backgroundColor: 'rgba(54, 162, 235, 0.5)',
        borderColor: 'rgba(54, 162, 235, 1)',
        borderWidth: 1
      }
    ]
  } : null;

  // Chart data for Top Days
  const topDaysChartData = topDays.data?.success ? {
    labels: topDays.data.data.map(item => item.date),
    datasets: [
      {
        label: 'Completed Requests',
        data: topDays.data.data.map(item => item.completed_count),
        fill: false,
        borderColor: 'rgba(75, 192, 192, 1)',
        backgroundColor: 'rgba(75, 192, 192, 0.2)',
        tension: 0.3,
        pointRadius: 3,
        pointHoverRadius: 6
      }
    ]
  } : null;

  // Chart data for Top Services
  const topServicesChartData = topServices.data?.success ? {
    labels: topServices.data.data.map(item => item.service),
    datasets: [
      {
        label: 'Success Rate (%)',
        data: topServices.data.data.map(item => item.success_rate),
        backgroundColor: 'rgba(75, 192, 192, 0.5)',
        borderColor: 'rgba(75, 192, 192, 1)',
        borderWidth: 1
      }
    ]
  } : null;

  const languageItems = languages.data?.success ? (languages.data.data || []) : [];
  const languageTableTotalPages = Math.max(1, Math.ceil(languageItems.length / LANGUAGE_TABLE_PAGE_SIZE));
  const languageTablePageSafe = Math.min(languageTablePage, languageTableTotalPages);
  const paginatedLanguageItems = languageItems.slice(
    (languageTablePageSafe - 1) * LANGUAGE_TABLE_PAGE_SIZE,
    languageTablePageSafe * LANGUAGE_TABLE_PAGE_SIZE
  );

  const languagesChartData = languageItems.length ? {
    labels: languageItems.map((item) => getLanguageDisplayName(item.language)),
    datasets: [
      {
        label: 'Completed Requests by Language',
        data: languageItems.map((item) => item.completed_count),
        backgroundColor: [
          'rgba(255, 99, 132, 0.5)',
          'rgba(54, 162, 235, 0.5)',
          'rgba(255, 206, 86, 0.5)',
          'rgba(75, 192, 192, 0.5)',
          'rgba(153, 102, 255, 0.5)',
          'rgba(255, 159, 64, 0.5)',
          'rgba(201, 203, 207, 0.5)',
          'rgba(83, 102, 255, 0.5)',
        ],
        borderColor: [
          'rgba(255, 99, 132, 1)',
          'rgba(54, 162, 235, 1)',
          'rgba(255, 206, 86, 1)',
          'rgba(75, 192, 192, 1)',
          'rgba(153, 102, 255, 1)',
          'rgba(255, 159, 64, 1)',
          'rgba(201, 203, 207, 1)',
          'rgba(83, 102, 255, 1)',
        ],
        borderWidth: 1,
      },
    ],
  } : null;

  // Chart data for Request Types (Pie Chart)
  const requestTypesChartData = requestTypes.data?.success ? {
    labels: requestTypes.data.data.map(item => item.type),
    datasets: [
      {
        label: 'Request Types',
        data: requestTypes.data.data.map(item => item.completed_count),
        backgroundColor: [
          'rgba(255, 99, 132, 0.5)',
          'rgba(54, 162, 235, 0.5)',
          'rgba(255, 206, 86, 0.5)',
          'rgba(75, 192, 192, 0.5)',
          'rgba(153, 102, 255, 0.5)',
          'rgba(255, 159, 64, 0.5)'
        ],
        borderColor: [
          'rgba(255, 99, 132, 1)',
          'rgba(54, 162, 235, 1)',
          'rgba(255, 206, 86, 1)',
          'rgba(75, 192, 192, 1)',
          'rgba(153, 102, 255, 1)',
          'rgba(255, 159, 64, 1)'
        ],
        borderWidth: 1
      }
    ]
  } : null;

  // Chart data for Processing Time
  const processingTimeChartData = processingTime.data?.success ? {
    labels: processingTime.data.data.map(item => item.service),
    datasets: [
      {
        label: 'Average Processing Time (minutes)',
        data: processingTime.data.data.map(item => item.avg_processing_minutes),
        backgroundColor: 'rgba(153, 102, 255, 0.5)',
        borderColor: 'rgba(153, 102, 255, 1)',
        borderWidth: 1
      }
    ]
  } : null;

  // Chart data for Last 7 Days Done & Failed Requests
  const last7DaysDoneChartData = last7DaysDone.data?.success ? {
    labels: last7DaysDone.data.data.map(item => {
      const date = new Date(item.date);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }),
    datasets: [
      {
        label: 'Done (Success)',
        data: last7DaysDone.data.data.map(item => item.completed_count ?? 0),
        backgroundColor: 'rgba(75, 192, 192, 0.6)',
        borderColor: 'rgba(75, 192, 192, 1)',
        borderWidth: 2,
        borderRadius: 4
      },
      {
        label: 'Failed',
        data: last7DaysDone.data.data.map(item => item.failed_count ?? 0),
        backgroundColor: 'rgba(255, 99, 132, 0.6)',
        borderColor: 'rgba(255, 99, 132, 1)',
        borderWidth: 2,
        borderRadius: 4
      }
    ]
  } : null;

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top'
      },
      title: {
        display: false
      },
      tooltip: {
        enabled: true
      },
      datalabels: {
        display: false
      }
    },
    scales: {
      y: {
        beginAtZero: true
      }
    }
  };

  const paymentRevenueBarOptions = (fullLabels = []) => ({
    ...chartOptions,
    indexAxis: 'y',
    plugins: {
      ...chartOptions.plugins,
      legend: { display: false },
      tooltip: {
        enabled: true,
        callbacks: {
          title: (items) => fullLabels[items[0]?.dataIndex] || items[0]?.label || '',
        },
      },
      datalabels: {
        display: true,
        clip: false,
        anchor: 'end',
        align: 'right',
        offset: 4,
        color: '#566a7f',
        font: { weight: '700', size: 11 },
        formatter: (value) => formatUsdLabel(value),
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        title: { display: true, text: 'USD' },
        grid: { display: true },
      },
      y: {
        ticks: {
          font: { size: 11 },
          autoSkip: false,
        },
      },
    },
  });

  const pieChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'right'
      },
      title: {
        display: false
      },
      tooltip: {
        enabled: true,
        callbacks: {
          label: function(context) {
            const label = context.label || '';
            const value = context.parsed || 0;
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = ((value / total) * 100).toFixed(1);
            return `${label}: ${value} (${percentage}%)`;
          }
        }
      }
    }
  };

  const lineChartOptions = {
    ...chartOptions,
    scales: {
      x: {
        title: {
          display: true,
          text: 'Date'
        }
      },
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: 'Completed Requests'
        }
      }
    }
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="mb-0">Song Generation Statistics</h2>
        <Button
          variant="warning"
          onClick={handleClearCache}
          disabled={clearCacheState.loading}
        >
          {clearCacheState.loading ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Clearing...
            </>
          ) : (
            'Clear Cache'
          )}
        </Button>
      </div>

      {(clearCacheState.data?.success && !clearCacheState.error) || clearCacheState.error ? (
        <div className="d-flex flex-column mb-4" style={{ gap: '1rem' }}>
          {clearCacheState.data?.success && !clearCacheState.error && (
            <Alert
              variant="success"
              onClose={() => {}}
              dismissible
              style={{
                borderRadius: '8px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                margin: 0,
                border: '1px solid rgba(0,0,0,0.08)'
              }}
            >
              {clearCacheState.data.message || 'Cache cleared successfully. Data refreshed.'}
            </Alert>
          )}

          {clearCacheState.error && (
            <Alert
              variant="danger"
              onClose={() => {}}
              dismissible
              style={{
                borderRadius: '8px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                margin: 0,
                border: '1px solid rgba(0,0,0,0.08)'
              }}
            >
              {clearCacheState.error || 'Failed to clear cache. Please try again.'}
            </Alert>
          )}
        </div>
      ) : null}

      {/* Last 7 Days Done & Failed Requests Chart */}
      <Row className="mb-4">
        <Col md={12}>
          <Card>
            <Card.Body>
              <Card.Title>Last 7 Days - Done &amp; Failed Requests</Card.Title>
              {last7DaysDone.loading ? (
                <div className="d-flex justify-content-center align-items-center" style={{ height: 300 }}>
                  <Spinner animation="border" />
                </div>
              ) : last7DaysDoneChartData ? (
                <div style={{ height: 300 }}>
                  <Bar data={last7DaysDoneChartData} options={{
                    ...chartOptions,
                    scales: {
                      x: {
                        title: {
                          display: true,
                          text: 'Date'
                        }
                      },
                      y: {
                        beginAtZero: true,
                        title: {
                          display: true,
                          text: 'Requests'
                        },
                        ticks: {
                          stepSize: 1
                        }
                      }
                    },
                    plugins: {
                      ...chartOptions.plugins,
                      tooltip: {
                        enabled: true,
                        callbacks: {
                          label: function(context) {
                            const row = last7DaysDone.data?.data?.[context.dataIndex];
                            const total = (row?.completed_count ?? 0) + (row?.failed_count ?? 0);
                            const pct = total > 0 ? ((context.parsed.y / total) * 100).toFixed(1) : 0;
                            return `${context.dataset.label}: ${context.parsed.y} (${pct}%)`;
                          }
                        }
                      }
                    }
                  }} />
                </div>
              ) : (
                <div style={{ height: 300 }} className="d-flex align-items-center justify-content-center text-muted">
                  {last7DaysDone.error || 'No data available'}
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Top Devices Chart */}
      <Row className="mb-4">
        <Col md={6}>
          <Card>
            <Card.Body>
              <Card.Title>Top Devices by Completed Requests</Card.Title>
              {topDevices.loading ? (
                <div className="d-flex justify-content-center align-items-center" style={{ height: 300 }}>
                  <Spinner animation="border" />
                </div>
              ) : topDevicesChartData ? (
                <div style={{ height: 300 }}>
                  <Bar data={topDevicesChartData} options={chartOptions} />
                </div>
              ) : (
                <div style={{ height: 300 }} className="d-flex align-items-center justify-content-center text-muted">
                  No data available
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Top Days Chart */}
        <Col md={6}>
          <Card>
            <Card.Body>
              <Card.Title>Top Days by Completed Requests</Card.Title>
              {topDays.loading ? (
                <div className="d-flex justify-content-center align-items-center" style={{ height: 300 }}>
                  <Spinner animation="border" />
                </div>
              ) : topDaysChartData ? (
                <div style={{ height: 300 }}>
                  <Line data={topDaysChartData} options={lineChartOptions} />
                </div>
              ) : (
                <div style={{ height: 300 }} className="d-flex align-items-center justify-content-center text-muted">
                  No data available
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Top Services and Request Types */}
      <Row className="mb-4">
        <Col md={6}>
          <Card>
            <Card.Body>
              <Card.Title>Top Services by Success Rate</Card.Title>
              {topServices.loading ? (
                <div className="d-flex justify-content-center align-items-center" style={{ height: 300 }}>
                  <Spinner animation="border" />
                </div>
              ) : topServicesChartData ? (
                <div style={{ height: 300 }}>
                  <Bar data={topServicesChartData} options={{
                    ...chartOptions,
                    scales: {
                      y: {
                        beginAtZero: true,
                        max: 100,
                        title: {
                          display: true,
                          text: 'Success Rate (%)'
                        }
                      }
                    }
                  }} />
                </div>
              ) : (
                <div style={{ height: 300 }} className="d-flex align-items-center justify-content-center text-muted">
                  No data available
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Request Types Chart */}
        <Col md={6}>
          <Card>
            <Card.Body>
              <Card.Title>Request Type Analysis</Card.Title>
              {requestTypes.loading ? (
                <div className="d-flex justify-content-center align-items-center" style={{ height: 300 }}>
                  <Spinner animation="border" />
                </div>
              ) : requestTypesChartData ? (
                <div style={{ height: 300 }}>
                  <Doughnut data={requestTypesChartData} options={pieChartOptions} />
                </div>
              ) : (
                <div style={{ height: 300 }} className="d-flex align-items-center justify-content-center text-muted">
                  No data available
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Content language distribution */}
      <Row className="mb-4">
        <Col md={6}>
          <Card>
            <Card.Body>
              <Card.Title>Requests by Content Language</Card.Title>
              <div className="text-muted small mb-3">
                Completed requests with detected lyrics language (content_language).
              </div>
              {languages.loading ? (
                <div className="d-flex justify-content-center align-items-center" style={{ height: 300 }}>
                  <Spinner animation="border" />
                </div>
              ) : languagesChartData ? (
                <div style={{ height: 300 }}>
                  <Doughnut data={languagesChartData} options={pieChartOptions} />
                </div>
              ) : (
                <div style={{ height: 300 }} className="d-flex align-items-center justify-content-center text-muted">
                  {languages.error || 'No language data yet. Run song:detect-languages backfill if needed.'}
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
        <Col md={6}>
          <Card>
            <Card.Body>
              <Card.Title>Language breakdown</Card.Title>
              {languages.loading ? (
                <div className="d-flex justify-content-center py-5">
                  <Spinner animation="border" />
                </div>
              ) : languageItems.length ? (
                <>
                  <div className="table-responsive">
                    <table className="table table-sm mb-0">
                      <thead>
                        <tr>
                          <th>Language</th>
                          <th className="text-end">Completed</th>
                          <th className="text-end">Share</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedLanguageItems.map((item) => (
                          <tr key={item.language}>
                            <td>
                              {getLanguageDisplayName(item.language)}
                              <span className="text-muted ms-1">({item.language})</span>
                            </td>
                            <td className="text-end">{item.completed_count}</td>
                            <td className="text-end">{item.percentage}%</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {languageItems.length > LANGUAGE_TABLE_PAGE_SIZE && (
                    <Pagination
                      currentPage={languageTablePageSafe}
                      totalItems={languageItems.length}
                      itemsPerPage={LANGUAGE_TABLE_PAGE_SIZE}
                      onPageChange={setLanguageTablePage}
                      position="bottom"
                    />
                  )}
                </>
              ) : (
                <div className="text-muted py-4 text-center">
                  {languages.error || 'No language data available'}
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Processing Time Chart */}
      <Row className="mb-4">
        <Col md={12}>
          <Card>
            <Card.Body>
              <Card.Title>Average Processing Time by Service</Card.Title>
              {processingTime.loading ? (
                <div className="d-flex justify-content-center align-items-center" style={{ height: 300 }}>
                  <Spinner animation="border" />
                </div>
              ) : processingTimeChartData ? (
                <div style={{ height: 300 }}>
                  <Bar data={processingTimeChartData} options={{
                    ...chartOptions,
                    scales: {
                      y: {
                        beginAtZero: true,
                        title: {
                          display: true,
                          text: 'Average Processing Time (minutes)'
                        }
                      }
                    }
                  }} />
                </div>
              ) : (
                <div style={{ height: 300 }} className="d-flex align-items-center justify-content-center text-muted">
                  No data available
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {showPaymentStats && (
        <>
          <hr className="my-4" />
          <h4 className="mb-3">Payment revenue (Production, USD)</h4>
          <Row className="mb-4">
            <Col md={12}>
              <Card>
                <Card.Body>
                  <Card.Title>Revenue last 7 days</Card.Title>
                  <div className="text-muted small mb-3">
                    Daily totals for production purchases (same metric as the Payments page chart).
                  </div>
                  {last7DaysRevenue.loading ? (
                    <div className="d-flex justify-content-center py-5">
                      <Spinner animation="border" />
                    </div>
                  ) : last7DaysRevenueChartData ? (
                    <div className="revenue-chart-wrap" style={{ height: 320 }}>
                      <Bar data={last7DaysRevenueChartData} options={last7DaysRevenueChartOptions} />
                    </div>
                  ) : (
                    <div className="text-muted py-4 text-center">
                      {last7DaysRevenue.error || 'No revenue data for the last 7 days'}
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
          <Row className="mb-4">
            <Col md={6}>
              <Card>
                <Card.Body>
                  <Card.Title>Top paying devices</Card.Title>
                  {topPayingDevices.loading ? (
                    <div className="d-flex justify-content-center py-5"><Spinner animation="border" /></div>
                  ) : topPayingDevicesChartData ? (
                    <div className="revenue-chart-wrap" style={{ height: 300 }}>
                      <Bar
                        data={topPayingDevicesChartData}
                        options={paymentRevenueBarOptions(
                          topPayingDevicesItems.map((item) => item.device_id)
                        )}
                      />
                    </div>
                  ) : (
                    <div className="text-muted py-4 text-center">No payment data yet</div>
                  )}
                </Card.Body>
              </Card>
            </Col>
            <Col md={6} xs={12}>
              <Card>
                <Card.Body>
                  <Card.Title>Top products by revenue</Card.Title>
                  {topPaymentProducts.loading ? (
                    <div className="d-flex justify-content-center py-5"><Spinner animation="border" /></div>
                  ) : topPaymentProductsChartData ? (
                    <div
                      className="revenue-chart-wrap"
                      style={{ height: Math.max(300, topPaymentProductItems.length * 40) }}
                    >
                      <Bar
                        data={topPaymentProductsChartData}
                        options={paymentRevenueBarOptions(topPaymentProductFullLabels)}
                      />
                    </div>
                  ) : (
                    <div className="text-muted py-4 text-center">No payment data yet</div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </>
      )}

    </div>
  );
};

export default Statistics;
